import { getEmbedAwarePath } from './embedStore';
import { isArsenalSessionContext } from './sessionContext';

let inactivityTimer: NodeJS.Timeout | null = null;
let lastActivityTime = Date.now();

// const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes en millisecondes
const INACTIVITY_TIMEOUT = 25 * 60 * 1000; // 25 minutes en millisecondes

// Détecte si l'app tourne en mode embed BPM via la clé sessionStorage dp_embed_mode.
// C'est la source canonique (cf. embedStore.ts) : elle est posée à l'ouverture du tab
// et survit aux rechargements OIDC dans le même onglet.
function isEmbedMode(): boolean {
  try {
    return window.sessionStorage.getItem('dp_embed_mode') === 'true';
  } catch {
    return false;
  }
}

// Fonction pour déconnecter l'utilisateur
function logoutUser() {
  const win: { __INACTIVITY_DEBUG__?: boolean } | undefined =
    typeof window !== 'undefined' ? (window as unknown as { __INACTIVITY_DEBUG__?: boolean }) : undefined;
  if (win?.__INACTIVITY_DEBUG__) {
    // eslint-disable-next-line no-console
    console.log('🚪 Déconnexion automatique pour inactivité');
  }

  // Lire le flag embed AVANT de vider le store (synchrone, pas de race condition)
  const embed = isEmbedMode();

  // Import dynamique pour éviter les problèmes de cycle.
  // La redirection est placée DANS le .then() pour garantir que le store est vidé
  // avant la navigation (pas de session fantôme sur la prochaine page).
  import('./userStore').then(({ useUserStore }) => {
    const { clearUser } = useUserStore.getState();
    if (typeof clearUser === 'function') clearUser();
    try {
      window.localStorage.removeItem('remember_me');
    } catch (e) {
      // ignore
    }

    // En mode embed/BPM : rediriger directement vers /auth/bpm pour relancer le
    // flux OIDC automatiquement. L'utilisateur étant toujours connecté côté BPM,
    // la reconnexion est transparente (pas d'écran de login affiché).
    window.location.href = embed ? getEmbedAwarePath('/auth/bpm') : '/auth';
  });

  // Import dynamique pour le toast
  import('react-hot-toast').then((toast) => {
    if (embed) {
      // En mode BPM/embed : message discret car la reconnexion sera automatique
      toast.default('Reconnexion BPM en cours…', { duration: 3000 });
    } else {
      toast.default.error('Session expirée pour cause d\'inactivité. Veuillez vous reconnecter.', {
        duration: 5000
      });
    }
  });
}

// Fonction pour réinitialiser le timer d'inactivité
function resetInactivityTimer() {
  lastActivityTime = Date.now();
  
  // Annuler le timer existant
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }
  
  // Créer un nouveau timer
  inactivityTimer = setTimeout(() => {
    // L'inactivité seule ne ferme plus les sessions internes (site normal / TSLH).
    if (!isArsenalSessionContext()) return;

    const win: { __INACTIVITY_DEBUG__?: boolean } | undefined =
      typeof window !== 'undefined' ? (window as unknown as { __INACTIVITY_DEBUG__?: boolean }) : undefined;
    if (win?.__INACTIVITY_DEBUG__) {
      // eslint-disable-next-line no-console
      console.log('⏰ Timeout d\'inactivité atteint (5 minutes)');
    }
    logoutUser();
  }, INACTIVITY_TIMEOUT);
}

// Fonction pour détecter l'activité utilisateur
function handleUserActivity() {
  const win: { __INACTIVITY_DEBUG__?: boolean } | undefined =
    typeof window !== 'undefined' ? (window as unknown as { __INACTIVITY_DEBUG__?: boolean }) : undefined;
  if (win?.__INACTIVITY_DEBUG__) {
    // eslint-disable-next-line no-console
    console.log('👆 Activité utilisateur détectée');
  }
  resetInactivityTimer();
}

// Fonction pour démarrer le monitoring d'inactivité
export function startInactivityMonitoring() {
  const win: { __INACTIVITY_DEBUG__?: boolean } | undefined =
    typeof window !== 'undefined' ? (window as unknown as { __INACTIVITY_DEBUG__?: boolean }) : undefined;
  if (win?.__INACTIVITY_DEBUG__) {
    // eslint-disable-next-line no-console
    console.log('🎯 Démarrage du monitoring d\'inactivité (5 minutes)');
  }
  
  // Événements à surveiller pour détecter l'activité
  const events = [
    'mousedown',
    'mousemove', 
    'keypress',
    'scroll',
    'touchstart',
    'click'
  ];
  
  // Ajouter les listeners
  events.forEach(event => {
    document.addEventListener(event, handleUserActivity, true);
  });
  
  // Démarrer le timer initial
  resetInactivityTimer();
  
  // Nettoyage lors de la fermeture de la page
  window.addEventListener('beforeunload', () => {
    stopInactivityMonitoring();
  });
}

// Fonction pour arrêter le monitoring
export function stopInactivityMonitoring() {
  const win: { __INACTIVITY_DEBUG__?: boolean } | undefined =
    typeof window !== 'undefined' ? (window as unknown as { __INACTIVITY_DEBUG__?: boolean }) : undefined;
  if (win?.__INACTIVITY_DEBUG__) {
    // eslint-disable-next-line no-console
    console.log('🛑 Arrêt du monitoring d\'inactivité');
  }
  
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }
  
  // Retirer tous les listeners
  const events = [
    'mousedown',
    'mousemove',
    'keypress', 
    'scroll',
    'touchstart',
    'click'
  ];
  
  events.forEach(event => {
    document.removeEventListener(event, handleUserActivity, true);
  });
}

// Fonction pour obtenir le temps restant avant déconnexion
export function getRemainingTime(): number {
  const elapsed = Date.now() - lastActivityTime;
  const remaining = INACTIVITY_TIMEOUT - elapsed;
  return Math.max(0, remaining);
}

// Fonction pour vérifier si l'utilisateur est actif
export function isUserActive(): boolean {
  return getRemainingTime() > 0;
}

// Fonction de debug
export function debugInactivity() {
  const remaining = getRemainingTime();
  const minutes = Math.floor(remaining / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);
  
  // Always log on explicit debug call
  // eslint-disable-next-line no-console
  console.log('🕒 Inactivité Debug:');
  // eslint-disable-next-line no-console
  console.log(`⏰ Temps restant: ${minutes}m ${seconds}s`);
  // eslint-disable-next-line no-console
  console.log(`📅 Dernière activité: ${new Date(lastActivityTime).toLocaleTimeString()}`);
  // eslint-disable-next-line no-console
  console.log(`🎯 Timer actif: ${inactivityTimer ? 'Oui' : 'Non'}`);
}

// Fonction pour forcer une déconnexion (test)
export function forceInactivityLogout() {
  const win: { __INACTIVITY_DEBUG__?: boolean } | undefined =
    typeof window !== 'undefined' ? (window as unknown as { __INACTIVITY_DEBUG__?: boolean }) : undefined;
  if (win?.__INACTIVITY_DEBUG__) {
    // eslint-disable-next-line no-console
    console.log('🧪 Test: Déconnexion forcée pour inactivité');
  }
  logoutUser();
}

// Exposer les fonctions pour debug dans la console
declare global {
  interface Window {
    debugInactivity?: typeof debugInactivity;
    forceInactivityLogout?: typeof forceInactivityLogout;
    getRemainingTime?: typeof getRemainingTime;
    setInactivityDebug?: (enabled: boolean) => void;
    __INACTIVITY_DEBUG__?: boolean;
  }
}

if (typeof window !== 'undefined') {
  window.debugInactivity = debugInactivity;
  window.forceInactivityLogout = forceInactivityLogout;
  window.getRemainingTime = getRemainingTime;
  window.setInactivityDebug = (enabled: boolean) => {
    window.__INACTIVITY_DEBUG__ = enabled;
  };
}
